import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Bubble, Segment } from 'tea-component';
import CardSlotV2 from '@/pages/intelligence/components/CardSlotV2';
import ArrowdownText from '../../../../components/ArrowdownText';
import {
  useFollowingOptionList,
  getActiveuserTitle,
} from '@/pages/intelligence/pages/Following/_hook/useFollowingOptionList';
import {
  checkAvailability,
  extractLineMetrics,
  extractBarMetrics,
  handleTypeMetrics,
  isFollowingRetentionMetricKey,
  getSelectedFollowingRetentionMetrics,
  getActiveFollowingRetentionMetric,
  getFollowingRetentionDnMetricKey,
  FOLLOWING_REVIEW_CHART_SEGMENT_KEY,
  getFollowingReviewMetric,
} from '@/pages/intelligence/pages/Following/_config/util';
import { useIntl, getLocale } from 'umi';
import VisuailsCommon from './VisuailsCommon';
import VisualisAnnieSensortower from './VisualisAnnieSensortower';
import { valueFormat } from '@/pages/intelligence/utils/commonUtils';
import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';
import TableNodata from '@/pages/intelligence/pages/Following/components/TableNodata';

const SEGMENTS = ['dau', 'revenue', 'download'];
export default function NewGamesVisualis(props: any) {
  const {
    APPID,
    metriceValue,
    entityType,
    dateType,
    isSingleMonth,
    handleChartType,
    handleSegementVal,
    barOrPieData,
    lineData,
    isVisualisLoading,
    fetchParams,
    sortByName,
    cardSlotTitle,
    segmentAllOptions,
    onClickNodata,
    isGlobal,
  } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();

  const {
    // followingChartSegmentOpts,
    retentionDNOptions,
    // followingEchartTitle,
  } = useFollowingOptionList();
  // const segmentAllOptions = followingChartSegmentOpts[entityType];
  const [options, setOptions] = useState(segmentAllOptions || []);
  const [segmentVal, setSegmentVal] = useState(segmentAllOptions?.[0]?.value || '');
  const [subSegmentVal, setSubSegmentVal] = useState(retentionDNOptions?.[0]?.value || '');
  const [chartType, setChartType] = useState('bar');
  const [activeuserMetric, setActiveuserMetric] = useState('');
  const [annieSensortowerEchart, setAnnieSensortowerEchart] = useState(false);
  const [titleNow, setTitleNow] = useState('');
  const [aggObj, setAggObj] = useState({});
  const [avgTitle, setAvgTitle] = useState('');
  const activeRetentionMetric = useMemo(
    () => getActiveFollowingRetentionMetric(metriceValue),
    [JSON.stringify(metriceValue)],
  );

  /* 勾选的游戏：
  判断是要展示sum、avg、max的计算方式
  acu, retention展示AVG，  pcu, dau展示MAX， 其他展示Sum
  Activer User、Revenue、Download双数据源情况下展示：
    Max DAU: 2个数据源里的最大值
    Sum Revenue: 数据源1的总和，数据源2的总和
    Sum Download: 数据源1的总和，数据源2的总和
  */
  useEffect(() => {
    if (chartType === 'line' || chartType === 'bar') {
      const extractMetricsFunction = chartType === 'line' ? extractLineMetrics : extractBarMetrics;
      const data = chartType === 'line' ? lineData : barOrPieData;
      const calMaxAvgSumObj = handleTypeMetrics(
        extractMetricsFunction,
        data,
        isFollowingRetentionMetricKey(segmentVal)
          ? getFollowingRetentionDnMetricKey(activeRetentionMetric, subSegmentVal)
          : segmentVal,
        metriceValue,
      );
      setAggObj(calMaxAvgSumObj);
    }
  }, [
    JSON.stringify(lineData),
    JSON.stringify(barOrPieData),
    JSON.stringify(metriceValue),
    segmentVal,
    subSegmentVal,
    chartType,
    activeRetentionMetric,
  ]);

  useEffect(() => {
    // 可视化标题展示，如果是Retention则不修改
    if (!isFollowingRetentionMetricKey(segmentVal)) {
      const optItem = segmentAllOptions?.find((metric: any) => {
        return metric.value === segmentVal;
      });
      let avgTitle = optItem?.text;
      if (segmentVal === 'dau') {
        avgTitle = getActiveuserTitle(dateType, formatMessage);
      }
      setAvgTitle(avgTitle);
      setTitleNow(optItem?.text);
    } else {
      setTitleNow(subSegmentVal);
      setAvgTitle(subSegmentVal);
    }
  }, [locale, segmentVal, dateType, subSegmentVal]);

  // Metrics / 时间筛选变化后的 segment 自动联动（不请求 trend，由 getGameData 统一触发）
  const handleChartSegementOfMetrics = (isInitialCall = false) => {
    const retentionLabel = formatMessage({ id: 'dbi_retention' });
    const opts = segmentAllOptions.map((option: any) => {
      const metricsAvailable = checkAvailability(metriceValue, option?.value);
      let disabled = !metricsAvailable;
      let text = option.text;

      if (isFollowingRetentionMetricKey(option.value)) {
        const retentionDisabled = !metricsAvailable || !isSingleMonth;
        if (retentionDisabled) {
          disabled = true;
          text = (
            <Bubble dark content={formatMessage({ id: 'dbi_info_following_retention' })}>
              {/** 加宽hover热区 */}
              <div style={{ margin: '-6px -12px', padding: '6px 12px' }}>
                {retentionLabel}
              </div>
            </Bubble>
          );
        } else {
          text = retentionLabel;
        }
      }

      return { ...option, disabled, text };
    });

    const retentionOption = opts.find((option: any) => isFollowingRetentionMetricKey(option.value) && !option.disabled);
    const shouldAutoSelectRetention = isInitialCall && isSingleMonth && !!retentionOption;

    let currentSegement: string | undefined;
    if (shouldAutoSelectRetention) {
      currentSegement = retentionOption!.value;
    } else {
      const lastOption = opts.find((option: any) => option.value === segmentVal);
      const lastSegementAvailable =
        lastOption?.disabled !== true && checkAvailability(metriceValue, segmentVal);
      const firstEnabledOption = opts.find((option: any) => option.disabled !== true);
      currentSegement = lastSegementAvailable ? segmentVal : firstEnabledOption?.value;
    }

    const segmentValToSet = currentSegement || segmentAllOptions?.[0]?.value || '';
    setSegmentVal(segmentValToSet);
    if (isFollowingRetentionMetricKey(segmentValToSet)) {
      setSubSegmentVal(retentionDNOptions?.[0]?.value);
      if (chartType !== 'line') {
        setChartType('line');
      }
      handleSegementVal(activeRetentionMetric, 'line', isFollowingRetentionMetricKey(segmentVal));
    } else if (segmentValToSet && segmentValToSet !== segmentVal) {
      handleSegementVal(segmentValToSet, chartType, true);
    }
    setOptions(opts);
  };

  const showEchartOneOrTwo = () => {
    if (isFollowingRetentionMetricKey(segmentVal)) {
      const selectedRetention = getSelectedFollowingRetentionMetrics(metriceValue);
      setAnnieSensortowerEchart(selectedRetention.length === 2);
      setActiveuserMetric('');
      return;
    }

    let activeMetric = '';
    let segmentCount = 0;
    // 判断Metric筛选器是勾选了2个还是1个，决定是展示双示例可视化还是单视例
    if (SEGMENTS.includes(segmentVal)) {
      metriceValue.forEach((t: any) => {
        if (t.includes(segmentVal)) {
          segmentCount++;
          if (segmentCount === 1) {
            activeMetric = t;
          }
        }
      });

      if (segmentCount === 1) {
        setActiveuserMetric(activeMetric);
      }
      // 能找到两个就设为true，否则设为false
      setAnnieSensortowerEchart(segmentCount === 2);
    } else {
      setAnnieSensortowerEchart(false);
    }
  };

  const initialCallRef = useRef(true);

  useEffect(() => {
    handleChartSegementOfMetrics(initialCallRef.current);
    initialCallRef.current = false;
  }, [JSON.stringify(metriceValue), entityType, isSingleMonth, locale, dateType, activeRetentionMetric]);

  useEffect(() => {
    showEchartOneOrTwo();
  }, [JSON.stringify(metriceValue), segmentVal, subSegmentVal]);

  const dualSourceChartMetric = isFollowingRetentionMetricKey(segmentVal)
    ? subSegmentVal
    : segmentVal;

  const getProcessedSegmentValue = () => {
    if (isFollowingRetentionMetricKey(segmentVal)) {
      return getFollowingRetentionDnMetricKey(activeRetentionMetric, subSegmentVal);
    }
    if (segmentVal === FOLLOWING_REVIEW_CHART_SEGMENT_KEY) {
      return getFollowingReviewMetric(metriceValue) || segmentVal;
    }
    if (SEGMENTS.includes(segmentVal)) {
      return activeuserMetric;
    }
    return segmentVal;
  };

  const chartsInstance = useRef<IChartsIndexInstance>(null);
  const isStreamingNodata =
    ['stream_hoursWatched', 'stream_peackCCV'].includes(segmentVal) && !isGlobal;

  return (
    <>
      <CardSlotV2
        title={
          <div style={{ display: 'flex' }}>
            <span style={{ marginRight: 10 }}>{cardSlotTitle}</span>
            <ArrowdownText isNew={true} sortedData={sortByName} order={fetchParams?.order} />
          </div>
        }
        className="dbi-following-card"
        disableChartType={isFollowingRetentionMetricKey(segmentVal) ? ['bar', 'pie'] : []}
        activeTab={chartType}
        getChange={(value: any) => {
          setChartType(value);
          handleChartType(value);
        }}
      >
        <div className="dbi-following-card-flex">
          <Segment
            className="dbi-following-segment"
            value={segmentVal}
            onChange={(value) => {
              setSegmentVal(value);
              if (isFollowingRetentionMetricKey(value)) {
                setChartType('line');
                handleSegementVal(activeRetentionMetric, 'line');
              } else {
                handleSegementVal(value, chartType);
              }
            }}
            options={options}
          />
          {/* <ArrowdownText
            isNew={true}
            sortedData={sortByName}
            order={fetchParams?.order}
          /> */}

          {/* <ArrowdownText text="Sorted by data.ai DAU" /> */}
        </div>
        {isFollowingRetentionMetricKey(segmentVal) && (
          <div className="dbi-following-card-flex">
            {/** 选择retention时 有子页签D2, D3, D7, D15, D31 */}
            <Segment
              className="dbi-following-segment"
              value={subSegmentVal}
              onChange={(value) => {
                setSubSegmentVal(value);
                setTitleNow(value);
              }}
              options={retentionDNOptions}
            />
          </div>
        )}
        <div className="data-chart">
          {annieSensortowerEchart ? (
            <VisualisAnnieSensortower
              APPID={APPID}
              barOrPieData={barOrPieData}
              lineData={lineData}
              metric={dualSourceChartMetric}
              chartType={chartType}
              entityType={entityType}
              isVisualisLoading={isVisualisLoading}
              dateType={dateType}
              titleNow={titleNow}
              chartsInstance={chartsInstance}
              fetchParams={fetchParams}
            />
          ) : !isVisualisLoading && isStreamingNodata ? (
            <TableNodata
              onClickNodata={onClickNodata}
              info={formatMessage({ id: 'dbi_info_following_echart_streaming_nodata' })}
              handleInfo={formatMessage({
                id: 'dbi_info_following_echart_streaming_backto_orginal',
              })}
            />
          ) : (
            <VisuailsCommon
              APPID={APPID}
              barOrPieData={barOrPieData}
              lineData={lineData}
              metric={getProcessedSegmentValue()}
              chartType={chartType}
              entityType={entityType}
              isVisualisLoading={isVisualisLoading}
              dateType={dateType}
              titleNow={titleNow}
              chartsInstance={chartsInstance}
              fetchParams={fetchParams}
            />
          )}
        </div>
      </CardSlotV2>
    </>
  );
}
